// src/Lexprompt/pages/PromptCard.jsx

import React, {
  useContext,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../user/AuthContext";
import "./PromptCard.css";

// ─── Helpers to detect media ────────────────────────────
function isImageUrl(url) {
  return /\.(jpeg|jpg|png|gif|webp)(\?.*)?$/i.test(url);
}
function isVideoUrl(url) {
  return /\.(mp4|webm|ogg)(\?.*)?$/i.test(url);
}
function isAudioUrl(url) {
  return /\.(mp3|wav|ogg|m4a|aac)(\?.*)?$/i.test(url);
}

// Use forwardRef so parent can call instance methods (openSharePopup, scrollIntoView)
export default forwardRef(function PromptCard(
  { prompt, isTemplate = false, highlight = false },
  ref
) {
  const navigate = useNavigate();
  const currentUser = useContext(AuthContext);

  const {
    id,
    title,
    department = "",
    body = "",
    imageUrl = "",
    videoUrl = "",
    audioUrl = "",      // ← ADD THIS
    attachment = "",
  } = prompt || {};

  // Determine which media URL to use (attachment > imageUrl > videoUrl)
  const mediaUrl = attachment || imageUrl || videoUrl || audioUrl;
  const hasImage = mediaUrl && isImageUrl(mediaUrl);
  const hasVideo = mediaUrl && isVideoUrl(mediaUrl);
  const hasAudio = mediaUrl && isAudioUrl(mediaUrl);

  // ─── Local UI state ─────────────────────────────────────
  const [isPrompt, setIsPrompt] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showShareUrl, setShowShareUrl] = useState(false);
  const [copied, setCopied] = useState(false);
  const [audioGlow, setAudioGlow] = useState(false);

  // Ref to the card’s root DOM node (for scrollIntoView)
  const domRef = useRef(null);

  // Expose instance methods so parent can scroll this card into view / open share pop‐up
  useImperativeHandle(ref, () => ({
    openSharePopup: () => setShowShareUrl(true),
    scrollIntoView: () => {
      if (domRef.current && domRef.current.scrollIntoView) {
        domRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    },
  }));

  // ─── When this card is “highlighted” (shareId matches id):
  // 1) scroll into view
  // 2) open the share‐URL pop‐up (URL + copy only)
  // 3) open the separate full‐screen media modal if any media exists
  useEffect(() => {
    if (highlight && domRef.current) {
      setTimeout(() => {
        if (domRef.current.scrollIntoView) {
          domRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        // show just the share‐URL pop‐up:
        setShowShareUrl(true);

               /* open the big modal ONLY for image / video.
                   Pure-audio cards just turn on the rainbow outline. */
                if (hasImage || hasVideo) {
                  setShowModal(true);
                }
                if (hasAudio && !hasImage && !hasVideo) {
                  setAudioGlow(true);
                }
      }, 100);
    }
  }, [highlight, mediaUrl]);

  if (!prompt) return null;

  // Build the “short link” (e.g. http://localhost:3000/p/abcd1234)
  const shortUrl = `${window.location.origin}/p/${id}`;

  // ─── Handlers ─────────────────────────────────────────
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(body);
  };

  const toggleHandler = (e) => {
    e.stopPropagation();
    setIsPrompt(e.target.checked);
  };

  const handleShareToggle = (e) => {
    e.stopPropagation();
    setShowShareUrl((prev) => !prev);
  };

  const handleCopyUrl = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(shortUrl);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
      setShowShareUrl(false);
    }, 1500);
  };

   const handleMediaClick = (e) => {
       e.stopPropagation();
    
       // 🔽 For audio-only cards toggle the glow; img/video = open modal
      if (hasAudio && !hasImage && !hasVideo) {
         setAudioGlow((g) => !g);
       } else {
         setShowModal(true);
       }
     };

  // ─── Render ────────────────────────────────────────────
  return (
    <div className="prompt-card" id={`card-${id}`} ref={domRef}>
      <div className="prompt-card-content">
        {/* ─── Header: Title, Department, Toggle, Share ─────── */}
        <div className="prompt-card-header">
          <div className="prompt-card-text">
              <h3
    className="prompt-title"
    style={{ cursor: "pointer" }}
    onClick={() => navigate(`${id}`)}
  >
    {title}
  </h3>
            <p className="prompt-category">{department}</p>
          </div>
          <div className="prompt-card-actions">
            {/* Only show the “prompt mode” toggle if there’s any media */}
            {mediaUrl && (
              <>
                <input
                  type="checkbox"
                  id={`toggle-${id}`}
                  className="switch-checkbox"
                  checked={isPrompt}
                  onChange={toggleHandler}
                  onClick={(e) => e.stopPropagation()}
                />
                <label htmlFor={`toggle-${id}`} className="switch-track">
                  <span className="switch-thumb" />
                </label>
              </>
            )}

            {/* ─── Share Button ────────────────────────────────────── */}
            <div className="prompt-share-wrapper">
              <button
                className="prompt-share-button"
                onClick={handleShareToggle}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#6b7280"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                </svg>
              </button>

              {/* ─── Share URL Pop‐up (URL + Copy only) ───────────────── */}
              {showShareUrl && (
                <div className="prompt-share-url-capsule">
                  {copied ? (
                    <span className="copied-label">Copied!</span>
                  ) : (
                    <>
                      {/* The short URL appears as plain text, not a link – clicking doesn’t navigate away */}
                      <span
                        className="share-url-text"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowShareUrl(true);
                        }}
                        style={{ cursor: "pointer", textDecoration: "underline" }}
                      >
                        {shortUrl}
                      </span>
                      <button
                        className="copy-url-icon"
                        onClick={handleCopyUrl}
                      >
                        📋
                      </button>
                    </>
                  )}


                  
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ─── Main Content: Media Thumbnail or Prompt Text ─── */}
        <div className="media-code-wrapper">
          {mediaUrl && !isPrompt ? (
             <div
               className={
                 "prompt-media-container" + (audioGlow ? " audio-glow" : "")
               }
              onClick={handleMediaClick}
            >
              {hasImage ? (
                <img
                  src={mediaUrl}
                  alt={title}
                  className="prompt-media-img"
                />
              ) : hasVideo ? (
                <video
                  src={mediaUrl}
                  className="prompt-media-video"
                  controls
                />
              ) : hasAudio ? (
                <audio
                  src={mediaUrl}
                  className="prompt-media-audio"
                  controls
                />
              ) : (
                <a
                  href={mediaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="prompt-media-link"
                >
                  Download Media
                </a>
              )}
            </div>
          ) : (
            <div className="prompt-code-container">
              {(!mediaUrl || isPrompt) && (
                <button
                  className="prompt-copy-button"
                  onClick={handleCopy}
                  aria-label="Copy prompt"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="#6b7280"
                    strokeWidth="1.5"
                    className="copy-svg"
                  >
                    <rect x="4" y="4" width="12" height="12" rx="1" />
                    <rect x="6" y="6" width="12" height="12" rx="1" />
                  </svg>
                </button>
              )}
              <pre className="prompt-code-block">
                <code>{body}</code>
              </pre>
            </div>
          )}
        </div>
      </div>

      {/* ─── Full-screen “Media Modal” Overlay ─────────────────────────── */}
      {showModal && (
        <div
          className="prompt-modal-overlay"
          onClick={() => setShowModal(false)}
        >
          <div
            className="prompt-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            {hasImage && <img src={mediaUrl} alt="Full preview" />}
            {hasVideo && (
              <video src={mediaUrl} controls autoPlay />
            )}
         
            <button
              className="prompt-modal-close"
              onClick={() => setShowModal(false)}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
});
